/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Pagination, Search, Typography2 } from '@coursera/cds-core';

import { learnerTableStyles } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/learnerTableStyles';
import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import useLearnerList, { LEARNER_LIST_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  learners: Learner[];
  onEdit: () => void;
  onBack: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  searchRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
  `,
  searchInput: css`
    flex: 1;

    /* CDS Search has an intrinsic width and doesn't expand to fill its
       flex parent on its own. Force both the outer wrapper and the inner
       MUI input root (which carries a min-width: 240px) to stretch so the
       search bar sits flush with the Edit button per design. */
    .cds-Search-base,
    .cds-Search-inputRoot {
      width: 100%;
    }
  `,
};

/**
 * Read-only view of the current learner cohort. Search filters the
 * displayed list client-side; "Edit learner list" hands off to the editor
 * sub-screen.
 */
const LearnerListScreen = ({ learners, onEdit, onBack }: Props) => {
  const { searchQuery, setSearchQuery, page, setPage, pageCount, pageRows, filteredCount } = useLearnerList({
    list: learners,
  });

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <div css={styles.searchRow}>
            <div css={styles.searchInput}>
              <Search
                aria-label={_t('Search learners')}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={_t('Search learners')}
                hideSearchButton
                data-testid="ai-message-modal-learner-list-search"
              />
            </div>
            <Button variant="secondary" onClick={onEdit} data-testid="ai-message-modal-learner-list-edit">
              {_t('Edit learner list')}
            </Button>
          </div>

          <div css={learnerTableStyles.tableWrapper}>
            <table
              css={learnerTableStyles.table}
              aria-label={_t('Learner list')}
              data-testid="ai-message-modal-learner-list-table"
            >
              <thead>
                <tr>
                  <th css={[learnerTableStyles.th, learnerTableStyles.nameColumn]}>
                    <Typography2 component="span" variant="subtitleMedium" color="supportText">
                      {_t('Name')}
                    </Typography2>
                  </th>
                  <th css={learnerTableStyles.th}>
                    <Typography2 component="span" variant="subtitleMedium" color="supportText">
                      {_t('Email')}
                    </Typography2>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td css={learnerTableStyles.emptyRow} colSpan={2}>
                      <Typography2 component="span" variant="bodySecondary">
                        {_t('No learners match your search.')}
                      </Typography2>
                    </td>
                  </tr>
                ) : (
                  pageRows.map(({ learner }) => (
                    <tr key={learner.id} data-testid={`ai-message-modal-learner-row-${learner.id}`}>
                      <td css={learnerTableStyles.td}>
                        <Typography2 component="span" variant="bodyPrimary">
                          {learner.name}
                        </Typography2>
                      </td>
                      <td css={learnerTableStyles.td}>
                        <Typography2 component="span" variant="bodyPrimary">
                          {learner.email}
                        </Typography2>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div css={learnerTableStyles.paginationRow}>
                <Pagination
                  total={filteredCount}
                  pageSize={LEARNER_LIST_PAGE_SIZE}
                  page={page}
                  onPageChange={(_event, nextPage) => setPage(nextPage)}
                  hidePageSizeInput
                  aria-label={_t('Learner list pagination')}
                />
              </div>
            )}
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={learnerTableStyles.actions}>
          <Button variant="secondary" onClick={onBack} data-testid="ai-message-modal-learner-list-back">
            {_t('Back')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default LearnerListScreen;
